import { Injectable, inject } from '@angular/core';
import { MatIconRegistry } from '@angular/material/icon';
import { DomSanitizer } from '@angular/platform-browser';

interface IconDefinition {
    /** assignment for MatIcon.svgIcon */
    name: string;
    /**
     * path to the SVG file.
     * requirements:
     *  1. must be SVG.
     *  2. must be in assets/mat-icons root directory or any subdirectory within.
     */
    path: `assets/mat-icons/${string}.svg`;
}

/**
 * Global service for registering custom SVG files with Angular Material for use with MatIcon.
 */
@Injectable({ providedIn: 'root' })
export class AppIconRegistryService {
    private domSanitizer = inject(DomSanitizer);
    private matIconRegistry = inject(MatIconRegistry);

    private hasBeenInitialized = false;

    /** start the SVG registration process. guarded against multiple executions in a single app lifetime. */
    start() {
        if (!this.hasBeenInitialized) {
            this.icons.forEach((icon) => {
                this.matIconRegistry.addSvgIcon(icon.name, this.domSanitizer.bypassSecurityTrustResourceUrl(icon.path));
            });
            this.hasBeenInitialized = true;
        }
    }

    /**
     * list of all icons. try to keep the structure in alignment with the actual folder :)
     * helpful to have Amazon Q check this when changed
     */
    private icons: IconDefinition[] = [
        { name: 'add', path: 'assets/mat-icons/add.svg' },
        { name: 'add_user', path: 'assets/mat-icons/add_user.svg' },
        { name: 'advanced', path: 'assets/mat-icons/advanced.svg' },
        { name: 'advocacy', path: 'assets/mat-icons/advocacy.svg' },
        { name: 'ai', path: 'assets/mat-icons/ai.svg' },
        { name: 'app_logo', path: 'assets/mat-icons/app_logo.svg' },
        { name: 'arrow_right', path: 'assets/mat-icons/arrow_right.svg' },
        { name: 'arrow_forward', path: 'assets/mat-icons/arrow-forward.svg' },
        { name: 'award', path: 'assets/mat-icons/award.svg' },
        { name: 'bullseye', path: 'assets/mat-icons/bullseye.svg' },
        { name: 'bullhorn', path: 'assets/mat-icons/bullhorn.svg' },
        { name: 'bar_chart', path: 'assets/mat-icons/bar_chart.svg' },
        { name: 'beginner', path: 'assets/mat-icons/beginner.svg' },
        { name: 'calendar', path: 'assets/mat-icons/calendar.svg' },
        { name: 'camera', path: 'assets/mat-icons/camera.svg' },
        { name: 'check', path: 'assets/mat-icons/check.svg' },
        { name: 'circle_check_mark', path: 'assets/mat-icons/circle_check_mark.svg' },
        { name: 'circle_check_mark_orange', path: 'assets/mat-icons/circle-checkmark-orange.svg' },
        { name: 'circle_exclamation', path: 'assets/mat-icons/circle_exclamation.svg' },
        { name: 'circle_info_solid', path: 'assets/mat-icons/circle_info_solid.svg' },
        { name: 'check_mark', path: 'assets/mat-icons/check_mark.svg' },
        { name: 'check_mark_2', path: 'assets/mat-icons/check_mark_2.svg' },
        { name: 'chevron_down', path: 'assets/mat-icons/chevron_down.svg' },
        { name: 'chevron_left', path: 'assets/mat-icons/chevron_left.svg' },
        { name: 'chevron_right', path: 'assets/mat-icons/chevron_right.svg' },
        { name: 'chevron_up', path: 'assets/mat-icons/chevron_up.svg' },
        { name: 'clock', path: 'assets/mat-icons/clock.svg' },
        { name: 'close', path: 'assets/mat-icons/close.svg' },
        { name: 'conversation', path: 'assets/mat-icons/conversation.svg' },
        { name: 'community', path: 'assets/mat-icons/community.svg' },
        { name: 'copy', path: 'assets/mat-icons/copy.svg' },
        { name: 'data_stack', path: 'assets/mat-icons/data_stack.svg' },
        { name: 'download', path: 'assets/mat-icons/download.svg' },
        { name: 'education', path: 'assets/mat-icons/education.svg' },
        { name: 'ellipsis', path: 'assets/mat-icons/ellipsis.svg' },
        { name: 'email', path: 'assets/mat-icons/email.svg' },
        { name: 'engage', path: 'assets/mat-icons/engage.svg' },
        { name: 'eye', path: 'assets/mat-icons/eye.svg' },
        { name: 'exclamation-mark', path: 'assets/mat-icons/exclamation-mark.svg' },
        { name: 'facebook', path: 'assets/mat-icons/facebook.svg' },
        { name: 'genesys_g', path: 'assets/mat-icons/genesys_g.svg' },
        { name: 'genesys_wave', path: 'assets/mat-icons/genesys_wave.svg' },
        { name: 'hamburger', path: 'assets/mat-icons/hamburger.svg' },
        { name: 'history', path: 'assets/mat-icons/history.svg' },
        { name: 'import', path: 'assets/mat-icons/import.svg' },
        { name: 'inactive', path: 'assets/mat-icons/inactive.svg' },
        { name: 'info_page', path: 'assets/mat-icons/info-page.svg' },
        { name: 'instagram', path: 'assets/mat-icons/instagram.svg' },
        { name: 'intermediate', path: 'assets/mat-icons/intermediate.svg' },
        { name: 'lightbulb', path: 'assets/mat-icons/lightbulb.svg' },
        { name: 'line_graph', path: 'assets/mat-icons/line_graph.svg' },
        { name: 'linkedin', path: 'assets/mat-icons/linkedin.svg' },
        { name: 'location', path: 'assets/mat-icons/location.svg' },
        { name: 'login', path: 'assets/mat-icons/login.svg' },
        { name: 'more_menu', path: 'assets/mat-icons/more-menu.svg' },
        { name: 'open_in_new', path: 'assets/mat-icons/open_in_new.svg' },
        { name: 'pencil', path: 'assets/mat-icons/pencil.svg' },
        { name: 'play', path: 'assets/mat-icons/play.svg' },
        { name: 'phone', path: 'assets/mat-icons/phone.svg' },
        { name: 'puzzle_piece', path: 'assets/mat-icons/puzzle_piece.svg' },
        { name: 'related', path: 'assets/mat-icons/related.svg' },
        { name: 'resources', path: 'assets/mat-icons/resources.svg' },
        { name: 'rewind_clock', path: 'assets/mat-icons/rewind_clock.svg' },
        { name: 'search', path: 'assets/mat-icons/search.svg' },
        { name: 'sort', path: 'assets/mat-icons/sort.svg' },
        { name: 'sort_ascending', path: 'assets/mat-icons/sort_ascending.svg' },
        { name: 'sort_descending', path: 'assets/mat-icons/sort_descending.svg' },
        { name: 'sparkles_solid', path: 'assets/mat-icons/sparkles-solid.svg' },
        { name: 'star-filled', path: 'assets/mat-icons/star-filled.svg' },
        { name: 'star-outline', path: 'assets/mat-icons/star-outline.svg' },
        { name: 'subtract', path: 'assets/mat-icons/subtract.svg' },
        { name: 'support', path: 'assets/mat-icons/support.svg' },
        { name: 'trash', path: 'assets/mat-icons/trash.svg' },
        { name: 'trophy', path: 'assets/mat-icons/trophy.svg' },
        { name: 'trophy-ranking', path: 'assets/mat-icons/trophy-ranking.svg' },
        { name: 'thumb_down', path: 'assets/mat-icons/thumb-down.svg' },
        { name: 'thumb_up', path: 'assets/mat-icons/thumb-up.svg' },
        { name: 'toggle', path: 'assets/mat-icons/toggle.svg' },
        { name: 'twitter_x', path: 'assets/mat-icons/twitter_x.svg' },
        { name: 'unlock', path: 'assets/mat-icons/unlock.svg' },
        { name: 'user', path: 'assets/mat-icons/user.svg' },
        { name: 'users', path: 'assets/mat-icons/users.svg' },
        { name: 'user_solid', path: 'assets/mat-icons/user-solid.svg' },
        { name: 'vector', path: 'assets/mat-icons/vector.svg' },
        { name: 'wave', path: 'assets/mat-icons/wave.svg' },
        { name: 'warning', path: 'assets/mat-icons/warning.svg' },
        { name: 'wordpress', path: 'assets/mat-icons/wordpress.svg' },
        { name: 'youtube', path: 'assets/mat-icons/youtube.svg' },
        { name: 'external-link', path: 'assets/mat-icons/external-link.svg' },
        { name: 'expansion-vector', path: 'assets/mat-icons/expansion-vector.svg' },
        { name: 'correct', path: 'assets/mat-icons/correct.svg' },
        { name: 'incorrect', path: 'assets/mat-icons/incorrect.svg' },
        { name: 'error_octagon', path: 'assets/mat-icons/error_octagon.svg' },
        { name: 'edit', path: 'assets/mat-icons/edit.svg' },
        { name: 'side_panel', path: 'assets/mat-icons/side_panel.svg' },
        { name: 'link', path: 'assets/mat-icons/link.svg' },
    ];
}
